import React from "react";
import LegalLayout, { LegalSection, LegalList } from "@/components/legal/LegalLayout";
import { Link } from "react-router-dom";
import {
  ShieldCheck, Lock, FileText, Server, Activity,
  HardDrive, Package, Mail, Download, FileDown,
} from "lucide-react";

/**
 * /trust — Public Trust Center.
 *
 * Aggregates Security, Privacy, Data Ownership, Reliability, Support, Legal
 * links plus downloadable procurement-grade PDFs. The PDFs are streamed
 * from the backend `/api/trust/*` endpoints (no auth).
 */
const API = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");

const DOWNLOADS = [
  {
    key: "whitepaper",
    title: "Security Whitepaper",
    blurb: "15-section procurement-review document. Identity, isolation, file security, storage, audit, operations, and roadmap.",
    href: `${API}/api/trust/security-whitepaper.pdf`,
    filename: "ForgedOps_Plans_Security_Whitepaper_v1.0.pdf",
  },
  {
    key: "ownership",
    title: "Data Ownership Statement",
    blurb: "Customer-data ownership posture. What ForgedOps does not claim and does not do with your data.",
    href: `${API}/api/trust/data-ownership.pdf`,
    filename: "ForgedOps_Plans_Data_Ownership_Statement_v1.0.pdf",
  },
  {
    key: "overview",
    title: "Security Overview",
    blurb: "Two-page executive summary suitable for procurement triage.",
    href: `${API}/api/trust/security-overview.pdf`,
    filename: "ForgedOps_Plans_Security_Overview_v1.0.pdf",
  },
  {
    key: "package",
    title: "Procurement Package · ZIP",
    blurb: "Everything above bundled — whitepaper, ownership statement, overview, Terms link, Privacy link.",
    href: `${API}/api/trust/procurement-package.zip`,
    filename: "ForgedOps_Plans_Procurement_Package_v1.0.zip",
    accent: true,
  },
];

const PILLARS = [
  { icon: Lock, title: "Security", to: "/legal/security",
    blurb: "TLS, JWT auth, RBAC, tenant isolation, audit, R2 storage, monitoring, backups." },
  { icon: ShieldCheck, title: "Privacy", to: "/legal/privacy",
    blurb: "What we collect, how it is used, retention, deletion, no-sale, customer ownership." },
  { icon: FileText, title: "Data Ownership", to: "#data-ownership",
    blurb: "Customers retain ownership of all uploaded data. ForgedOps acts only as a processor." },
  { icon: Activity, title: "Reliability", to: "#reliability",
    blurb: "Anonymous health endpoints, daily backups, durable object storage, incident response." },
  { icon: Mail, title: "Support", to: "/support",
    blurb: "support@forgedopshq.com · same-day response on production-impacting issues." },
  { icon: FileText, title: "Legal", to: "/legal/terms",
    blurb: "Terms of Use, Privacy Policy, Security disclosures — all public, dated January 1, 2026." },
];

const TOC = [
  { id: "pillars",        label: "Trust Pillars" },
  { id: "downloads",      label: "Document Downloads" },
  { id: "security",       label: "Security at a Glance" },
  { id: "data-ownership", label: "Data Ownership" },
  { id: "reliability",    label: "Platform Reliability" },
  { id: "contacts",       label: "Security Contacts" },
];

export default function TrustCenter() {
  return (
    <LegalLayout
      testid="page-trust"
      eyebrow="Trust · Public Center"
      title="ForgedOps Plans Trust Center"
      intro="Procurement-grade documentation for contractors, municipalities, counties, state agencies, airports, utility owners, CEI firms, and enterprise SaaS customers. Everything below is publicly accessible — no sales call required."
      toc={TOC}
      metaTitle="Trust Center · ForgedOps Plans"
      metaDescription="ForgedOps Plans Trust Center — security whitepaper, data ownership statement, security overview, and procurement package. Download without a sales meeting."
      canonical="https://forgedopsplans.com/trust"
    >
      {/* Pillars */}
      <section id="pillars" className="not-prose scroll-mt-24" data-testid="trust-pillars">
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {PILLARS.map((p) => {
            const Icon = p.icon;
            const inner = (
              <div
                className="h-full rounded-md border p-4 transition-colors"
                style={{ borderColor: "var(--fop-border)", background: "var(--fop-surface)" }}
              >
                <div className="flex items-center gap-2 mb-2">
                  <Icon size={16} style={{ color: "var(--fop-primary)" }} />
                  <div className="font-mono text-[10px] uppercase tracking-[0.22em]"
                        style={{ color: "var(--fop-text-3)" }}>
                    {p.title}
                  </div>
                </div>
                <div className="text-sm leading-relaxed" style={{ color: "var(--fop-text-2)" }}>
                  {p.blurb}
                </div>
              </div>
            );
            return p.to.startsWith("/") ? (
              <Link key={p.title} to={p.to} data-testid={`trust-pillar-${p.title.toLowerCase()}`}>{inner}</Link>
            ) : (
              <a key={p.title} href={p.to} data-testid={`trust-pillar-${p.title.toLowerCase()}`}>{inner}</a>
            );
          })}
        </div>
      </section>

      {/* Downloads */}
      <LegalSection id="downloads" title="Document Downloads">
        <p>Every document below is a printable, version-stamped PDF dated <strong>January 1, 2026</strong>. The procurement package bundles all three PDFs plus links to the current Terms of Use and Privacy Policy.</p>
        <div className="not-prose mt-4 grid sm:grid-cols-2 gap-3" data-testid="trust-downloads">
          {DOWNLOADS.map((d) => (
            <a
              key={d.key}
              href={d.href}
              download={d.filename}
              data-testid={`trust-download-${d.key}`}
              className="block rounded-md border p-4 transition-transform hover:-translate-y-px"
              style={{
                borderColor: d.accent ? "var(--fop-primary)" : "var(--fop-border)",
                background: "var(--fop-surface)",
              }}
            >
              <div className="flex items-center justify-between gap-3">
                <div className="flex items-center gap-2">
                  {d.key === "package"
                    ? <Package size={16} style={{ color: "var(--fop-primary)" }} />
                    : <FileDown size={16} style={{ color: "var(--fop-primary)" }} />}
                  <div className="font-display text-base" style={{ color: "var(--fop-text)" }}>
                    {d.title}
                  </div>
                </div>
                <Download size={14} style={{ color: "var(--fop-text-3)" }} />
              </div>
              <div className="text-sm mt-2 leading-relaxed" style={{ color: "var(--fop-text-2)" }}>
                {d.blurb}
              </div>
            </a>
          ))}
        </div>
      </LegalSection>

      <LegalSection id="security" title="Security at a Glance">
        <LegalList items={[
          "Transport — TLS 1.2+ for all traffic, HSTS enforced.",
          "Identity — JWT access tokens, rotated refresh tokens, salted bcrypt password hashing.",
          "Access — six tenant roles enforced server-side; platform admin gated by env allowlist.",
          "Isolation — every query scoped by tenant_id; cross-tenant probes return 404.",
          "Storage — Cloudflare R2, SSE-S3 encryption at rest, tenant-prefixed object keys, signed URLs only.",
          "Uploads — extension allowlist, magic-byte verification, macro-Office rejection, size caps, audit on reject.",
          "Audit — tamper-evident rows on every admin / security event, up to 7-year retention capable.",
          "Backups — daily database backups on a 90-day rolling window.",
        ]} />
        <p>Full control narrative + roadmap: <Link to="/legal/security" className="underline" style={{ color: "var(--fop-primary)" }}>/legal/security</Link>.</p>
      </LegalSection>

      <LegalSection id="data-ownership" title="Data Ownership">
        <p><strong>Customers retain ownership of all uploaded data.</strong> ForgedOps acts as a processor on the customer&rsquo;s behalf and never claims ownership of plans, drawings, photos, project records, exports, or audit data.</p>
        <p>ForgedOps does not sell, rent, license, or share customer data with third parties for marketing or advertising. Customer data is never used to train third-party AI models.</p>
        <p>For the complete statement, download <a className="underline" style={{ color: "var(--fop-primary)" }} href={`${API}/api/trust/data-ownership.pdf`} data-testid="data-ownership-inline-link">ForgedOps_Plans_Data_Ownership_Statement_v1.0.pdf</a>.</p>
      </LegalSection>

      <LegalSection id="reliability" title="Platform Reliability">
        <LegalList items={[
          "Anonymous health endpoint at /api/health — returns database, storage, and email-configuration status.",
          "Alternate health endpoint at /api/system_health — same payload for external monitoring tools.",
          "Daily database backups, 90-day rolling retention.",
          "Object storage on Cloudflare R2 — durable, replicated, independent of container lifecycle.",
          "Same-business-day response to production-impacting issues during U.S. Eastern business hours.",
        ]} />
      </LegalSection>

      <LegalSection id="contacts" title="Security Contacts">
        <div className="not-prose grid sm:grid-cols-3 gap-3">
          <ContactCard label="Support" address="support@forgedopshq.com" testid="trust-contact-support" />
          <ContactCard label="Security" address="security@forgedopshq.com" testid="trust-contact-security" />
          <ContactCard label="Information" address="info@forgedopshq.com" testid="trust-contact-info" />
        </div>
        <p>ForgedOps does not currently hold SOC 2, ISO 27001, FedRAMP, or HIPAA certification. Any future certification will be announced on this Trust Center. We do not claim certifications we have not obtained.</p>
      </LegalSection>
    </LegalLayout>
  );
}

function ContactCard({ label, address, testid }) {
  return (
    <div
      className="rounded-md border p-4"
      style={{ borderColor: "var(--fop-border)", background: "var(--fop-surface)" }}
      data-testid={testid}
    >
      <div className="font-mono text-[10px] uppercase tracking-[0.22em] mb-1"
            style={{ color: "var(--fop-text-3)" }}>{label}</div>
      <a href={`mailto:${address}`} className="underline text-sm"
          style={{ color: "var(--fop-primary)" }}>{address}</a>
    </div>
  );
}
